import {
  useWSModalCustom,
  useWSSnackbar,
  WSActions,
  WSAlert,
  WSCard,
  WSForm,
  WSInfoBox,
  WSList,
  WSModal,
  WSModalCustomProps,
  WSText
} from "@wingspanhq/fe-component-library";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import React, { useCallback } from "react";
import { PayeeEngagementType, WorkLog } from "@wingspanhq/wingspan-api-sdk";
import { queriesWorkLogs } from "@query";
import { ErrorAlert } from "@components/ErrorAlert";
import { PAYROLL_CURRENCY } from "../../../PayrollRuns/constants";
import { parsePayrollAmount } from "../../../PayrollRuns/utils";
import { PublicCommentField } from "../../components/PublicCommentField";
import { useMutationCancelWorkLogConvertedResource } from "../../hooks/useMutationCancelWorkLogConvertedResource";
import { useQueryWorkLogsReleasedByCancel } from "../../hooks/useQueryWorkLogsReleasedByCancel";
import { selectorWorkLogConvertedResourceLabels } from "../../selectors/selectorWorkLogConvertedResourceLabels";
import { selectorWorkLogLabel } from "../../selectors/selectorWorkLogLabel";
import { selectorWorkLogWorkerNoun } from "../../selectors/selectorWorkLogWorkerNoun";

type FormData = {
  comment: string;
};

type Props = {
  workLog: WorkLog;
  contractorName?: string;
  /** Names the person the cancellation is explained to. */
  engagementType?: PayeeEngagementType | null;
} & WSModalCustomProps<{
  success?: boolean;
}>;

export const ModalCancelWorkLogInvoice: React.FC<Props> = ({
  workLog,
  contractorName = "the contractor",
  engagementType,
  ...modalProps
}) => {
  const workerNoun = selectorWorkLogWorkerNoun(engagementType);
  const { openSnackbar } = useWSSnackbar();
  // A W-2 work log converts into a pay statement, a 1099 one into an invoice —
  // both the copy and the cancel endpoint follow `convertedResourceType`.
  // Only opened on an approved work log, so `convertedResourceType` is set.
  const { cancel: cancelLabel, noun } = selectorWorkLogConvertedResourceLabels({
    convertedResourceType: workLog.convertedResourceType
  });

  // The v1 combined payable-cancel (PATCH /payable/:id which also set the work
  // log to InReview) is split into two v3 calls: cancel the converted resource,
  // then reject the work log. `cancelPayStatement` documents the first half —
  // "cancelling detaches the statement's work logs (back to InReview)" — which
  // is the state `rejectWorkLog` requires.
  // TODO(v3): `cancelPayable` documents no equivalent side effect, so the
  // reject may be refused on the contractor side.
  const {
    cancelConvertedResource,
    isLoading: isCancelling,
    error: cancelError
  } = useMutationCancelWorkLogConvertedResource();
  const [rejectWorkLog, rejectMeta] =
    queriesWorkLogs.rejectWorkLog.useMutation();

  // One pay statement can pay several work logs; cancelling releases all of
  // them, and each then needs approving again separately.
  const { workLogs: releasedWorkLogs } =
    useQueryWorkLogsReleasedByCancel(workLog);

  const isLoading = isCancelling || rejectMeta.isLoading;

  const handleCancel = useCallback(
    async (formData: FormData) => {
      if (!formData.comment.trim()) {
        openSnackbar({
          type: "warning",
          message: "Please provide a comment for the cancellation."
        });
        return;
      }

      // TODO(v3): `invoiceId` renamed to `convertedResourceId` on v3 WorkLog.
      if (!workLog.convertedResourceId) {
        openSnackbar({
          type: "warning",
          message: `No linked ${noun} found for this work log.`
        });
        return;
      }

      // Step 1: Cancel the payable / pay statement. A failure leaves the modal
      // open with the server's own reason rendered below — a cancel can be
      // rejected for reasons the payer needs to read ("the pay statement has
      // been paid", "its payroll run is finalized"), and "please try again"
      // would send them round the same loop.
      const cancelled = await cancelConvertedResource(
        workLog,
        formData.comment
      );

      if (!cancelled) {
        return;
      }

      // Step 2: Reject the work log with the same comment
      rejectWorkLog(
        {
          workLogId: workLog.id!,
          rejectWorkLogRequest: { reason: formData.comment }
        },
        {
          onSuccess: () => {
            modalProps.onClose({ success: true });
            openSnackbar({
              icon: { name: "check" },
              message: `The ${noun} was cancelled.`
            });
          }
        }
      );
    },
    [
      openSnackbar,
      cancelConvertedResource,
      noun,
      rejectWorkLog,
      workLog,
      modalProps
    ]
  );

  return (
    <WSModal title={cancelLabel} {...modalProps}>
      <WSForm<FormData> onSubmit={handleCancel} defaultValues={{ comment: "" }}>
        <WSList gap="XL">
          <WSText.ParagraphSm color="gray500">
            {`Are you sure you want to cancel the ${noun}?`}
          </WSText.ParagraphSm>

          <WSInfoBox title="Funded for payment">
            {`If the ${noun} has been funded for payment already, the funds are returned to the payroll funding account when it is cancelled.`}
          </WSInfoBox>

          {releasedWorkLogs.length > 0 && (
            <>
              <WSAlert
                icon="alert"
                theme="warning"
                title={`${releasedWorkLogs.length} other work log${
                  releasedWorkLogs.length === 1 ? "" : "s"
                } will be released`}
              >
                {`This ${noun} also covers the work logs below. Cancelling it sends them back to awaiting approval, so you'll need to approve each of them again before they can be paid.`}
              </WSAlert>

              <WSCard>
                <WSList gap="S">
                  {releasedWorkLogs.map(released => (
                    <WSText.ParagraphSm key={released.id} color="gray600">
                      {`${selectorWorkLogLabel(released)} · ${formatMoney(
                        parsePayrollAmount(released.totalAmount),
                        PAYROLL_CURRENCY
                      )}`}
                    </WSText.ParagraphSm>
                  ))}
                </WSList>
              </WSCard>
            </>
          )}

          <PublicCommentField
            label="Comment *"
            description={`Comment is visible to both you and the ${workerNoun}. Please provide specific details that will inform ${contractorName} about the decision.`}
            placeholder="Provide specific details about the decision"
          />

          <WSAlert theme="warning" title={cancelLabel} icon="alert">
            This action cannot be undone
          </WSAlert>

          <ErrorAlert
            context="WorkLog"
            error={cancelError ?? rejectMeta.error}
          />

          <WSActions
            alignment="fill"
            buttons={[
              {
                kind: "Secondary",
                label: "Cancel",
                type: "button",
                onClick: () => {
                  modalProps.onClose({ success: false });
                }
              },
              {
                kind: "Primary",
                destructive: true,
                label: cancelLabel,
                loading: isLoading
              }
            ]}
          />
        </WSList>
      </WSForm>
    </WSModal>
  );
};

export const useModalCancelWorkLogInvoice = () =>
  useWSModalCustom(ModalCancelWorkLogInvoice);
